import React, { useMemo, useState } from "react";

/* Ceinture nouée vue de face : bande, deux pans qui retombent, nœud par-dessus */
const BeltIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 38" aria-hidden="true" focusable="false">
    <g
      fill="currentColor"
      stroke="rgba(0,0,0,0.32)"
      strokeWidth="1.4"
      strokeLinejoin="round"
    >
      <rect x="2" y="12" width="24" height="12" rx="2.5" />
      <rect x="38" y="12" width="24" height="12" rx="2.5" />
      <path d="M26 22h5.5l-1.5 13h-5.5z" />
      <path d="M32.5 22h5.5l1.5 13h-5.5z" />
      <rect x="24" y="11" width="16" height="14" rx="3.5" />
    </g>
  </svg>
);

/* Ordre des ceintures — l'index sert de seuil d'apparition d'une technique */
const BELTS = [
  { id: "jaune",  label: "Jaune",  color: "#f2e205", ink: "#0d0d0f", soft: "rgba(242,226,5,0.16)" },
  { id: "orange", label: "Orange", color: "#f5a623", ink: "#0d0d0f", soft: "rgba(245,166,35,0.16)" },
  { id: "verte",  label: "Verte",  color: "#7dc855", ink: "#0d0d0f", soft: "rgba(125,200,85,0.16)" },
  { id: "bleue",  label: "Bleue",  color: "#0070bf", ink: "#ffffff", soft: "rgba(0,112,191,0.16)" },
  { id: "marron", label: "Marron", color: "#7a5c11", ink: "#ffffff", soft: "rgba(122,92,17,0.16)" },
];

/* `from` = index de la première ceinture où la technique est exigée (cumulatif) */
const PROGRAM = [
  {
    part: "Tashi Waza",
    subtitle: "Techniques debout",
    icon: "🥋",
    families: [
      {
        name: "Ukemi",
        note: "Chutes",
        items: [
          { name: "Ushiro Ukemi", from: 0 },
          { name: "Yoko Ukemi", from: 0 },
          { name: "Mae Ukemi", from: 1 },
        ],
      },
      {
        name: "Déplacement",
        note: "Shintai",
        items: [
          { name: "Avant", from: 2 },
          { name: "Arrière", from: 2 },
          { name: "Rotation", from: 3 },
        ],
      },
      {
        name: "Tashi Waza",
        note: "Projections",
        items: [
          { name: "Seoi Nage", from: 0 },
          { name: "O Goshi", from: 0 },
          { name: "O Soto Gari", from: 0 },
          { name: "Uki Goshi", from: 1 },
          { name: "Koshi Guruma", from: 1 },
          { name: "De Ashi Barai", from: 1 },
          { name: "Hiza Guruma", from: 1 },
          { name: "Tai Otoshi", from: 2 },
          { name: "Harai Goshi", from: 2 },
          { name: "O Uchi Gari", from: 2 },
          { name: "Ko Uchi Gari", from: 2 },
          { name: "Sasae Tsurikomi Ashi", from: 3 },
          { name: "Okuri Ashi Barai", from: 3 },
          { name: "Uchi Mata", from: 3 },
          { name: "Tsurikomi Goshi", from: 4 },
          { name: "Kata Guruma", from: 4 },
          { name: "Uki Otoshi", from: 4 },
        ],
      },
      {
        name: "Nage No Kata",
        note: "Kata des projections",
        items: [
          { name: "Te Waza", from: 2 },
          { name: "Koshi Waza", from: 3 },
          { name: "Ashi Waza", from: 4 },
        ],
      },
    ],
  },
  {
    part: "Ne Waza",
    subtitle: "Techniques au sol",
    icon: "🤼",
    families: [
      {
        name: "Osaekomi Waza",
        note: "Immobilisations",
        items: [
          { name: "Hon Kesa Gatame", from: 0 },
          { name: "Hon Kesa Gatame", detail: "sortie", from: 1 },
          { name: "Hon Kesa Gatame", detail: "entrée et 3 défenses", from: 2 },
          { name: "Kuzure Kesa Gatame", from: 0 },
          { name: "Kuzure Kesa Gatame", detail: "sortie", from: 1 },
          { name: "Kuzure Kesa Gatame", detail: "entrée et 3 défenses", from: 2 },
          { name: "Kata Gatame", from: 0 },
          { name: "Kata Gatame", detail: "sortie", from: 1 },
          { name: "Kata Gatame", detail: "entrée et 3 défenses", from: 2 },
          { name: "Yoko Shiho Gatame", from: 1 },
          { name: "Yoko Shiho Gatame", detail: "sortie", from: 2 },
          { name: "Yoko Shiho Gatame", detail: "entrée et 3 défenses", from: 3 },
          { name: "Kuzure Yoko Shiho Gatame", from: 2 },
          { name: "Tate Shiho Gatame", from: 1 },
          { name: "Tate Shiho Gatame", detail: "sortie", from: 2 },
          { name: "Tate Shiho Gatame", detail: "entrée et 3 défenses", from: 3 },
          { name: "Kuzure Tate Shiho Gatame", from: 2 },
          { name: "Kami Shiho Gatame", from: 1 },
          { name: "Kami Shiho Gatame", detail: "sortie", from: 2 },
          { name: "Kami Shiho Gatame", detail: "entrée et 3 défenses", from: 3 },
          { name: "Kuzure Kami Shiho Gatame", from: 2 },
        ],
      },
      {
        name: "Shime Waza",
        note: "Étranglements",
        items: [
          { name: "Nami Juji Jime", from: 3 },
          { name: "Kata Juji Jime", from: 3 },
          { name: "Gyaku Juji Jime", from: 3 },
          { name: "Hadaka Jime", from: 4 },
          { name: "Okuri Eri Jime", from: 4 },
          { name: "Kataha Jime", from: 4 },
        ],
      },
      {
        name: "Kansetsu Waza",
        note: "Clés de bras",
        items: [
          { name: "Ude Garami", from: 3 },
          { name: "Ude Gatame", from: 3 },
          { name: "Juji Gatame", from: 3 },
          { name: "Hiza Gatame", from: 4 },
          { name: "Waki Gatame", from: 4 },
          { name: "Hara Gatame", from: 4 },
        ],
      },
    ],
  },
];

export default function CeinturesSection() {
  const [beltIndex, setBeltIndex] = useState(0);
  const belt = BELTS[beltIndex];

  const program = useMemo(
    () =>
      PROGRAM.map((part) => ({
        ...part,
        families: part.families
          .map((f) => ({ ...f, items: f.items.filter((it) => it.from <= beltIndex) }))
          .filter((f) => f.items.length > 0),
      })).filter((part) => part.families.length > 0),
    [beltIndex]
  );

  const total = useMemo(
    () =>
      program.reduce(
        (sum, part) => sum + part.families.reduce((s, f) => s + f.items.length, 0),
        0
      ),
    [program]
  );

  const fresh = useMemo(
    () =>
      program.reduce(
        (sum, part) =>
          sum + part.families.reduce((s, f) => s + f.items.filter((i) => i.from === beltIndex).length, 0),
        0
      ),
    [program, beltIndex]
  );

  return (
    <section
      id="ceintures"
      className="section"
      style={{ "--belt": belt.color, "--belt-soft": belt.soft, "--belt-ink": belt.ink }}
    >
      <div className="container">
        <div data-reveal="up">
          <span className="section-label">🎓 Passage de grade</span>
          <h2 className="section-title">Techniques par ceinture</h2>
          <p className="section-sub">
            Le programme officiel exigé à chaque grade. Choisis ta ceinture pour voir ce que tu dois maîtriser.
          </p>
        </div>

        <div className="belt-picker" role="tablist" aria-label="Choisir une ceinture" data-reveal="up">
          {BELTS.map((b, i) => (
            <button
              key={b.id}
              type="button"
              role="tab"
              aria-selected={i === beltIndex}
              className={`belt-chip ${i === beltIndex ? "active" : ""}`}
              style={{ "--chip": b.color, "--chip-ink": b.ink }}
              onClick={() => setBeltIndex(i)}
            >
              <BeltIcon className="belt-knot" />
              {b.label}
            </button>
          ))}
        </div>

        <div className="belt-summary" data-reveal="up">
          <BeltIcon className="belt-summary-icon" />
          <strong>{total}</strong> techniques au programme de la ceinture {belt.label.toLowerCase()}
          {fresh > 0 && (
            <>
              {" · "}
              <span className="belt-new-count">{fresh} nouvelles</span>
            </>
          )}
        </div>

        <div className="belt-parts">
          {program.map((part, pi) => (
            <div className="belt-part" key={part.part} data-reveal="up" data-delay={String(pi + 1)}>
              <div className="belt-part-head">
                <span className="belt-part-icon" aria-hidden="true">{part.icon}</span>
                <div>
                  <h3 className="belt-part-title">{part.part}</h3>
                  <span className="belt-part-sub">{part.subtitle}</span>
                </div>
              </div>

              {part.families.map((f) => (
                <div className="belt-family" key={part.part + f.name}>
                  <div className="belt-family-head">
                    <span className="belt-family-name">{f.name}</span>
                    <span className="belt-family-note">{f.note}</span>
                  </div>
                  <ul className="belt-techs">
                    {f.items.map((it, i) => (
                      <li
                        key={f.name + it.name + (it.detail || "") + i}
                        className={`belt-tech ${it.from === beltIndex ? "is-new" : ""}`}
                      >
                        <span className="tech-name">{it.name}</span>
                        {it.detail && <span className="tech-detail">{it.detail}</span>}
                        {it.from === beltIndex && <span className="tech-badge">nouveau</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>

        <p className="belt-legend">
          Le programme est cumulatif : chaque grade reprend les techniques des ceintures précédentes.
        </p>
      </div>
    </section>
  );
}
